// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { NavigationControls } from '@client/banks/layouts/navigation-controls';
import { ScreenLayout } from '@client/banks/layouts/screen-layout';
import { Button, Typography } from '@mui/material';
import { AppIcon } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { InstitutionCatalogPicker } from './institution-catalog-picker';
import styles from '../styles/institution-catalog.module.css';
import { useInstitutionSelectExisting } from './use-institution-select-existing';

export const InstitutionSelectExisting = defineComponent({
  displayName: 'InstitutionSelectExisting',
  useSetup: useInstitutionSelectExisting,
  View: ({
    catalog,
    isFormValid,
    onContinueFromCatalog,
    onSelectInstitution,
    goToManualEntry,
  }) => (
    <ScreenLayout
      actions={
        <NavigationControls
          previousStep="institution-manual"
          isFormValid={isFormValid}
          onNext={onContinueFromCatalog}
          nextStep="institution-manual"
        />
      }
    >
      <div className={styles['step-body']}>
        <form
          id="institution-select-existing-form"
          onSubmit={event => event.preventDefault()}
        >
          <fieldset className="m-0 flex min-w-0 flex-col gap-7 border-0 p-0">
            <header className={styles['step-header']}>
              <Typography
                component="h2"
                variant="h6"
                className={styles['step-title']}
              >
                <Translation
                  i18nKey="aggregator.create-bank.institution.title"
                  ns="aggregator"
                />
              </Typography>
              <Typography variant="body2" className={styles['step-description']}>
                <Translation
                  i18nKey="aggregator.create-bank.institution.catalog-description"
                  ns="aggregator"
                />
              </Typography>
            </header>

            <InstitutionCatalogPicker
              t={catalog.t}
              countryOptions={catalog.countryOptions}
              country={catalog.country}
              search={catalog.search}
              institutions={catalog.institutions}
              countryByCode={catalog.countryByCode}
              isLoading={catalog.isLoading}
              isError={catalog.isError}
              hasInstitutions={catalog.hasInstitutions}
              onRetry={catalog.onRetry}
              onCountryChange={catalog.onCountryChange}
              onSearchChange={catalog.onSearchChange}
              onSelectInstitution={onSelectInstitution}
            />
          </fieldset>
        </form>

        <Button
          type="button"
          variant="outlined"
          color="secondary"
          fullWidth
          className={styles['alternate-action']}
          startIcon={<AppIcon icon="Pen" size="lg" color="inherit" />}
          onClick={goToManualEntry}
          data-testid="bank-upload-institution-enter-manual"
        >
          <Translation
            i18nKey="aggregator.create-bank.institution.enter-manually"
            ns="aggregator"
          />
        </Button>
      </div>
    </ScreenLayout>
  ),
});
